import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DropdownFilter, FloatInput } from "src/componentes";
import { ConsultarPessoa } from "src/hook/Consulta/ConsultaPessoa.Module";
import PessoaCadastroView from "src/pages/Privada/Entidade/Pessoa/Cadastro/PessoaCadastro.View";
import { PessoaDto } from "src/pages/Privada/Entidade/Pessoa/Pessoa.Dto";
import { CTeDto, CTePessoaDto, getLocal, STORAGECTE_KEY, updateLocal } from "../CTe.Dto";
import { RemetenteFormType, RemetenteSchema } from "./CTe.Cadastro.Dto";

const CTeCadastroRemetenteView = () => {
  const navigate = useNavigate();
  const [cadastro, setCadastro] = useState<boolean>(false);
  const pessoaBuscar = ConsultarPessoa();
  const cte = getLocal<CTeDto>(STORAGECTE_KEY);
  const remetente = cte?.remetente;

  const form = useForm<RemetenteFormType>({
    resolver: yupResolver(RemetenteSchema),
    defaultValues: {
      documento: remetente?.documento ?? "",
      nome: remetente?.nome ?? "",
      nomeFantasia: remetente?.nomeFantasia ?? "",
      inscricaoEstadual: remetente?.inscricaoEstadual ?? "",
      endereco: remetente?.endereco ?? "",
      enderecoNumero: remetente?.enderecoNumero ?? "",
      bairro: remetente?.bairro ?? "",
      codigoMunicipio: remetente?.codigoMunicipio?.toString() ?? "",
      nomeMunicipio: remetente?.nomeMunicipio ?? "",
      uf: remetente?.uf ?? "",
      cep: remetente?.cep ?? "",
      codigoPais: remetente?.codigoPais ?? "1058",
      nomePais: remetente?.nomePais ?? "BRASIL",
    },
  });

  const handleVoltar = () => {
    navigate("../tributacao");
  };

  const handleSuccess = (value: PessoaDto) => {
    form.reset({
      documento: value.documento,
      nome: value.nome,
      nomeFantasia: value.nomeFantasia || value.nome,
      inscricaoEstadual: value.inscricaoEstadual || "",
      endereco: value.endereco,
      enderecoNumero: value.enderecoNumero,
      bairro: value.bairro,
      codigoMunicipio: value.codigoMunicipio?.toString() || "",
      nomeMunicipio: value.nomeMunicipio,
      uf: value.uf,
      cep: value.cep,
      codigoPais: value.codigoPais || "1058",
      nomePais: value.nomePais || "BRASIL",
    });
    setCadastro(false);
  };

  const handleCancel = () => {
    setCadastro(false);
  };

  const onSubmit = (data: RemetenteFormType) => {
    const remetenteDto: CTePessoaDto = {
      documento: data.documento,
      nome: data.nome,
      nomeFantasia: data.nomeFantasia || data.nome,
      inscricaoEstadual: data.inscricaoEstadual || "",
      endereco: data.endereco,
      enderecoNumero: data.enderecoNumero,
      bairro: data.bairro,
      codigoMunicipio: data.codigoMunicipio,
      nomeMunicipio: data.nomeMunicipio,
      uf: data.uf,
      cep: data.cep,
      codigoPais: data.codigoPais || "1058",
      nomePais: data.nomePais || "BRASIL",
    };

    updateLocal<CTeDto>(STORAGECTE_KEY, {
      remetente: remetenteDto,
    });

    navigate("../destinatario");
  };

  if (cadastro) {
    return (
      <Row className="mt-3">
        <PessoaCadastroView
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      </Row>
    );
  }

  const hasSelectedPessoa = !!form.watch("documento");

  return (
    <Form noValidate onSubmit={form.handleSubmit(onSubmit)} className="mt-3 mb-5">
      <h5 className="mb-3 text-primary">Dados do Remetente</h5>
      <Row className="g-3">
        <Col xs={12} md={10}>
          <DropdownFilter<PessoaDto>
            fetchData={pessoaBuscar.PessoaBuscar}
            loading={pessoaBuscar.loading}
            labelDescription="Pesquisar Pessoa / Empresa"
            getDescription={(item: PessoaDto) => `${item.nome} (${item.documento})`}
            getId={(item: PessoaDto) => item.id}
            registeredFields={{
              id: form.register("nome"),
              description: form.register("nome"),
            }}
            errorsMessageDescription={form.formState.errors.nome}
            onSelect={(item: PessoaDto | undefined) => {
              if (item) {
                form.setValue("documento", item.documento);
                form.setValue("nome", item.nome);
                form.setValue("nomeFantasia", item.nomeFantasia || item.nome);
                form.setValue("inscricaoEstadual", item.inscricaoEstadual || "");
                form.setValue("endereco", item.endereco);
                form.setValue("enderecoNumero", item.enderecoNumero);
                form.setValue("bairro", item.bairro);
                form.setValue("codigoMunicipio", item.codigoMunicipio?.toString() || "");
                form.setValue("nomeMunicipio", item.nomeMunicipio);
                form.setValue("uf", item.uf);
                form.setValue("cep", item.cep);
                form.setValue("codigoPais", item.codigoPais || "1058");
                form.setValue("nomePais", item.nomePais || "BRASIL");
              } else {
                form.reset({
                  documento: "",
                  nome: "",
                  nomeFantasia: "",
                  inscricaoEstadual: "",
                  endereco: "",
                  enderecoNumero: "",
                  bairro: "",
                  codigoMunicipio: "",
                  nomeMunicipio: "",
                  uf: "",
                  cep: "",
                  codigoPais: "1058",
                  nomePais: "BRASIL",
                });
              }
            }}
          />
        </Col>
        <Col xs={12} md={2} className="d-flex align-items-center">
          <Button variant="outline-primary" onClick={() => setCadastro(true)} className="w-100">
            Nova Pessoa
          </Button>
        </Col>
      </Row>

      {hasSelectedPessoa && (
        <Row className="g-3 mt-1">
          <Col xs={12} md={4}>
            <FloatInput
              label="Documento (CNPJ/CPF)"
              registerField={form.register("documento")}
              errorsMessage={form.formState.errors.documento}
              readOnly={true}
            />
          </Col>
          <Col xs={12} md={4}>
            <FloatInput
              label="Inscrição Estadual"
              registerField={form.register("inscricaoEstadual")}
              errorsMessage={form.formState.errors.inscricaoEstadual}
            />
          </Col>
          <Col xs={12} md={4}>
            <FloatInput
              label="Nome Fantasia"
              registerField={form.register("nomeFantasia")}
              errorsMessage={form.formState.errors.nomeFantasia}
            />
          </Col>

          <Col xs={12} md={6}>
            <FloatInput
              label="Endereço"
              registerField={form.register("endereco")}
              errorsMessage={form.formState.errors.endereco}
            />
          </Col>
          <Col xs={6} md={2}>
            <FloatInput
              label="Número"
              registerField={form.register("enderecoNumero")}
              errorsMessage={form.formState.errors.enderecoNumero}
            />
          </Col>
          <Col xs={6} md={4}>
            <FloatInput
              label="Bairro"
              registerField={form.register("bairro")}
              errorsMessage={form.formState.errors.bairro}
            />
          </Col>

          <Col xs={12} md={5}>
            <FloatInput
              label="Município"
              registerField={form.register("nomeMunicipio")}
              errorsMessage={form.formState.errors.nomeMunicipio}
              readOnly={true}
            />
          </Col>
          <Col xs={4} md={2}>
            <FloatInput
              label="UF"
              registerField={form.register("uf")}
              errorsMessage={form.formState.errors.uf}
              readOnly={true}
            />
          </Col>
          <Col xs={8} md={3}>
            <FloatInput
              label="CEP"
              registerField={form.register("cep")}
              errorsMessage={form.formState.errors.cep}
            />
          </Col>
          <Col xs={12} md={2}>
            <FloatInput
              label="País"
              registerField={form.register("nomePais")}
              errorsMessage={form.formState.errors.nomePais}
              readOnly={true}
            />
          </Col>
        </Row>
      )}

      <div className="fixed-bottom">
        <Container className="py-3">
          <Row>
            <Col xs={6} className="d-grid">
              <Button variant="outline-secondary" onClick={handleVoltar}>
                Voltar
              </Button>
            </Col>
            <Col xs={6} className="d-grid">
              <Button variant="outline-primary" type="submit">
                Próximo
              </Button>
            </Col>
          </Row>
        </Container>
      </div>
    </Form>
  );
};

export default CTeCadastroRemetenteView;
